import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import Utils from "../utils";

const stripHtml = (html = "") =>
  html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const slugify = (s = "") =>
  s
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 64) || "section";

const estimateReadingMinutes = (items = []) => {
  const words = items.reduce(
    (acc, it) => acc + stripHtml(it?.description || "").split(/\s+/).filter(Boolean).length,
    0
  );
  return Math.max(1, Math.round(words / 220));
};

const formatUpdated = (items = []) => {
  const stamps = items
    .map((it) => it?.updated_at || it?.created_at)
    .filter(Boolean)
    .map((s) => new Date(s))
    .filter((d) => !isNaN(d.getTime()));
  if (!stamps.length) return null;
  const latest = new Date(Math.max(...stamps.map((d) => d.getTime())));
  return latest.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const LegalPage = ({
  title,
  subtitle,
  fetcher,
  eyebrow = "Documento Legal",
  emptyMessage = "Ainda não há conteúdo disponível.",
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [openIds, setOpenIds] = useState(new Set());
  const [copiedSlug, setCopiedSlug] = useState(null);
  const cardRefs = useRef({});

  useEffect(() => {
    setLoading(true);
    fetcher()
      .then((r) => {
        const data = Array.isArray(r?.data?.data) ? r.data.data : [];
        const sorted = [...data].sort(
          (a, b) => (a.sequence || 0) - (b.sequence || 0)
        );
        setItems(sorted);

        // Honour deep-link hash (#sec-slug) by opening + scrolling to it.
        const hash = decodeURIComponent((window.location.hash || "").slice(1));
        const match = hash
          ? sorted.findIndex((it) => slugify(it.title || "") === hash)
          : -1;
        if (match >= 0) {
          const targetId = sorted[match].sequence ?? match;
          setOpenIds(new Set([targetId]));
          setTimeout(() => {
            const el = cardRefs.current[targetId];
            if (el) {
              const y = el.getBoundingClientRect().top + window.scrollY - 100;
              window.scrollTo({ top: y, behavior: "smooth" });
            }
          }, 200);
        } else if (sorted.length) {
          setOpenIds(new Set([sorted[0].sequence ?? 0]));
        }
      })
      .catch((err) => toast.error(Utils.getErrorMessage(err)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const readingMinutes = useMemo(() => estimateReadingMinutes(items), [items]);
  const updatedAt = useMemo(() => formatUpdated(items), [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const hay = `${it.title || ""} ${stripHtml(it.description || "")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, query]);

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenIds(new Set(items.map((it) => it.sequence ?? 0)));
  const collapseAll = () => setOpenIds(new Set());

  const highlight = (text = "") => {
    const q = query.trim();
    if (!q) return text;
    const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    const parts = text.split(re);
    return parts.map((p, i) =>
      re.test(p) ? (
        <mark key={i} className="tp-legal-hl">
          {p}
        </mark>
      ) : (
        <React.Fragment key={i}>{p}</React.Fragment>
      )
    );
  };

  return (
    <div className="tp-legal2">
      {/* Hero */}
      <div className="tp-legal2-hero">
        <div className="tp-legal2-hero-glow" aria-hidden="true" />
        <div className="tp-legal2-hero-inner">
          <span className="tp-legal2-eyebrow">{eyebrow}</span>
          <h1 className="tp-legal2-title">
            <span className="tp-legal2-title-grad">{title}</span>
          </h1>
          {subtitle ? <p className="tp-legal2-subtitle">{subtitle}</p> : null}

          <div className="tp-legal2-stats">
            {updatedAt && (
              <div className="tp-legal2-stat">
                <span className="tp-legal2-stat-icon">📅</span>
                <div>
                  <div className="tp-legal2-stat-label">Actualizado em</div>
                  <div className="tp-legal2-stat-value">{updatedAt}</div>
                </div>
              </div>
            )}
            {!!items.length && (
              <div className="tp-legal2-stat">
                <span className="tp-legal2-stat-icon">⏱</span>
                <div>
                  <div className="tp-legal2-stat-label">Leitura</div>
                  <div className="tp-legal2-stat-value">{readingMinutes} min</div>
                </div>
              </div>
            )}
            {!!items.length && (
              <div className="tp-legal2-stat">
                <span className="tp-legal2-stat-icon">📑</span>
                <div>
                  <div className="tp-legal2-stat-label">Secções</div>
                  <div className="tp-legal2-stat-value">{items.length}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toolbar: search + expand/collapse */}
      {!loading && !!items.length && (
        <div className="tp-legal2-toolbar">
          <div className="tp-legal2-search">
            <span className="tp-legal2-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Procurar no documento..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button
                type="button"
                className="tp-legal2-search-clear"
                onClick={() => setQuery("")}
                aria-label="Limpar"
              >
                ✕
              </button>
            )}
          </div>
          <div className="tp-legal2-toolbar-actions">
            <button type="button" className="tp-legal2-tool-btn" onClick={expandAll}>
              Expandir todas
            </button>
            <button type="button" className="tp-legal2-tool-btn" onClick={collapseAll}>
              Fechar todas
            </button>
            <button
              type="button"
              className="tp-legal2-tool-btn"
              onClick={() => window.print()}
            >
              🖨️ Imprimir
            </button>
          </div>
        </div>
      )}

      {/* Cards */}
      <div className="tp-legal2-list">
        {loading ? (
          <>
            <div className="tp-legal2-skeleton" />
            <div className="tp-legal2-skeleton" />
            <div className="tp-legal2-skeleton" />
          </>
        ) : !items.length ? (
          <div className="tp-legal2-empty">{emptyMessage}</div>
        ) : !filtered.length ? (
          <div className="tp-legal2-empty">
            Nenhum resultado para <strong>"{query}"</strong>.
          </div>
        ) : (
          filtered.map((it, idx) => {
            const id = it.sequence ?? idx;
            const isOpen = openIds.has(id);
            const num = String(it.sequence ?? idx + 1).padStart(2, "0");
            const slug = slugify(it.title || `section-${id}`);
            const copyLink = (ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              const url = `${window.location.origin}${window.location.pathname}#${slug}`;
              if (navigator.clipboard?.writeText) {
                navigator.clipboard
                  .writeText(url)
                  .then(() => {
                    setCopiedSlug(slug);
                    setTimeout(() => setCopiedSlug((s) => (s === slug ? null : s)), 1800);
                  })
                  .catch(() => toast.error("Não foi possível copiar o link."));
              } else {
                window.history.replaceState(null, "", `#${slug}`);
                toast.info("Link actualizado na barra de endereço.");
              }
              // Keep card state as-is; also reflect in URL
              window.history.replaceState(null, "", `#${slug}`);
            };
            return (
              <div
                key={id}
                id={slug}
                ref={(el) => (cardRefs.current[id] = el)}
                className={`tp-legal2-card${isOpen ? " open" : ""}`}
              >
                <div className="tp-legal2-card-head-wrap">
                  <button
                    type="button"
                    className="tp-legal2-card-head"
                    onClick={() => toggle(id)}
                    aria-expanded={isOpen}
                  >
                    <div className="tp-legal2-card-num">{num}</div>
                    <div className="tp-legal2-card-title">
                      {highlight(it.title || "Secção")}
                    </div>
                  </button>
                  <button
                    type="button"
                    className={`tp-legal2-card-copy${copiedSlug === slug ? " copied" : ""}`}
                    onClick={copyLink}
                    title="Copiar link para esta secção"
                    aria-label="Copiar link para esta secção"
                  >
                    {copiedSlug === slug ? "✓ Copiado" : "🔗"}
                  </button>
                  <button
                    type="button"
                    className="tp-legal2-card-chevron-btn"
                    onClick={() => toggle(id)}
                    aria-label={isOpen ? "Fechar" : "Abrir"}
                  >
                    <div className="tp-legal2-card-chevron">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 9L12 15L18 9"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </button>
                </div>
                <div className="tp-legal2-card-body">
                  <div className="tp-legal2-card-body-inner">
                    <div
                      className="tp-legal2-content"
                      dangerouslySetInnerHTML={{ __html: it.description || "" }}
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Contact CTA */}
      {!loading && !!items.length && (
        <div className="tp-legal2-cta">
          <div className="tp-legal2-cta-inner">
            <div>
              <h3>Tem dúvidas sobre este documento?</h3>
              <p>Fale com a nossa equipa. Estamos aqui para ajudar.</p>
            </div>
            <a href="mailto:geral@tp.software" className="tp-btn-primary-lg">
              geral@tp.software
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegalPage;
